<template>
  <!-- Login Page - Full screen without role bar or sidebar -->
  <div
    v-if="isLoginPage"
    class="min-h-screen w-screen overflow-x-hidden overflow-y-auto"
  >
    <router-view />
  </div>

  <!-- Main App Layout - With role bar and sidebar -->
  <div v-else>
    <!-- Role Indicator Bar - Shows on every page when authenticated -->
    <div
      v-if="isAuthenticated" 
      :class="roleBarClass" 
      class="h-8 max-h-8 min-h-8 w-full fixed top-0 left-0 right-0 z-50 transition-colors duration-300 shadow-lg overflow-hidden"
    >
      <div class="flex items-center justify-between h-full px-4">
        <div class="text-white text-sm font-medium">
          {{ roleDisplayName }}
        </div>
        <div class="text-white text-sm">
          {{ userDisplayName }}
        </div>
      </div>
    </div>

    <div
      class="flex h-screen bg-symphony-surface-ground overflow-x-hidden p-6" 
      :class="{ 'pt-20': isAuthenticated }"
    >
      <!-- Sidebar: freelancer gets their own sidebar -->
      <div
        v-if="!hideSidebar"
        class="flex-shrink-0 h-[calc(100vh-2.5rem)]"
      >
        <FreelancerSidebar v-if="showFreelancerSidebar" />
        <Sidebar v-else />
      </div>

      <!-- Main Content -->
      <div class="flex-1 w-full overflow-auto pl-4 pb-8">
        <div class="max-w-full">
          <router-view class="w-full" />
        </div>
      </div>
    </div>
  </div>
    
  <!-- Global Chat Window - only mount when open so overlay cannot persist when closed -->
  <ChatWindow
    v-if="isChatWindowVisible"
    v-model:visible="isChatWindowVisible"
    @close="closeChatWindow"
    @minimize="minimizeChatWindow"
  />
    
  <AppBuildFooter />

  <!-- Global ConfirmDialog -->
  <ConfirmDialog />

  <!-- Global Toast (for real-time notification toasts and other messages) -->
  <Toast />

  <!-- Reminder toasts: persistent, with snooze/reschedule/view -->
  <Toast
    group="reminder"
    position="top-right"
  >
    <template #message="slotProps">
      <div class="flex flex-col items-start flex-auto w-full gap-2">
        <div
          class="cursor-pointer w-full"
          @click="onReminderComplete(slotProps.message)"
        >
          <span class="font-semibold">{{ slotProps.message.summary }}</span>
          <span class="text-sm opacity-90 block">{{ slotProps.message.detail }}</span>
        </div>
        <div
          class="flex gap-2 flex-wrap"
          @click.stop
        >
          <Button
            label="Snooze 15 min"
            size="small"
            severity="secondary"
            outlined
            @click="onReminderSnooze(slotProps.message)"
          />
          <Button
            label="Reschedule"
            size="small"
            severity="secondary"
            outlined
            @click="onReminderReschedule(slotProps.message)"
          />
          <Button
            label="View"
            size="small"
            severity="secondary"
            outlined
            @click="onReminderToastClick(slotProps.message)"
          />
        </div>
      </div>
    </template>
  </Toast>

  <!-- Reschedule reminder dialog (opened from toast) -->
  <Dialog
    v-model:visible="rescheduleDialogVisible"
    header="Reschedule Reminder"
    :modal="true"
    :closable="true"
    :focus-on-show="false"
    class="w-full max-w-md"
    @hide="resetRescheduleForm"
  >
    <div class="space-y-4">
      <p class="text-sm text-gray-600">
        {{ rescheduleMessage?.detail || 'Set a new date and time for this reminder.' }}
      </p>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">New date & time</label>
        <DatePicker
          v-model="rescheduleDueAt"
          show-time
          hour-format="24"
          date-format="dd/mm/yy"
          :min-date="minRescheduleDate"
          placeholder="Select date and time"
          class="w-full"
          show-icon
          icon-display="input"
          fluid
        />
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          severity="secondary"
          @click="rescheduleDialogVisible = false"
        />
        <Button
          label="Reschedule"
          severity="primary"
          :disabled="!rescheduleDueAt"
          :loading="rescheduleSaving"
          @click="confirmReschedule"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watchEffect, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationsStore } from '@/stores/notificationsStore';
import { snoozeReminder, updateReminder } from '@/services/remindersService';
import AppBuildFooter from './components/shared/AppBuildFooter.vue';
import Sidebar from './components/sidebar/Sidebarv3.vue';
import FreelancerSidebar from './components/sidebar/FreelancerSidebar.vue';
import ChatWindow from './components/shared/ChatWindow.vue';
import { useChatWindow } from './composables/useChatWindow';
import { useNotificationToasts } from './composables/useNotificationToasts';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const toast = useToast();
const notificationsStore = useNotificationsStore();
const hideSidebar = ref(route.meta.hideSidebar);

// Reschedule dialog state
const rescheduleDialogVisible = ref(false);
const rescheduleMessage = ref(null);
const rescheduleDueAt = ref(null);
const rescheduleSaving = ref(false);
const minRescheduleDate = computed(() => new Date());

/** Mark reminder as read, dismiss toast – used when clicking main toast area. */
function onReminderComplete(message) {
    const data = message?.data;
    if (data?.type !== 'reminder') return;
    if (data.notificationId) notificationsStore.markAsRead(data.notificationId);
    toast.removeGroup('reminder');
}

/** Mark as read, navigate to project, dismiss – used by View button. */
function onReminderToastClick(message) {
    const data = message?.data;
    if (data?.type !== 'reminder' || !data.link) return;
    notificationsStore.markAsRead(data.notificationId);
    router.push(data.link);
    toast.removeGroup('reminder');
}

async function onReminderSnooze(message) {
    const data = message?.data;
    if (data?.type !== 'reminder' || !data.reminderId || !data.projectId) return;
    try {
        await snoozeReminder(data.projectId, data.reminderId, 15);
        notificationsStore.markAsRead(data.notificationId);
        toast.removeGroup('reminder');
        toast.add({
            severity: 'success',
            summary: 'Snoozed',
            detail: 'Reminder snoozed for 15 minutes',
            life: 4000
        });
    } catch (err) {
        const msg = err.response?.data?.message ?? 'Failed to snooze reminder';
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: msg,
            life: 6000
        });
    }
}

function onReminderReschedule(message) {
    const data = message?.data;
    if (data?.type !== 'reminder' || !data.reminderId || !data.projectId) return;
    rescheduleMessage.value = message;
    rescheduleDueAt.value = new Date(Date.now() + 15 * 60 * 1000);
    rescheduleDialogVisible.value = true;
}

function resetRescheduleForm() {
    rescheduleMessage.value = null;
    rescheduleDueAt.value = null;
    rescheduleSaving.value = false;
}

async function confirmReschedule() {
    const msg = rescheduleMessage.value;
    const data = msg?.data;
    if (!data || data?.type !== 'reminder' || !data.reminderId || !data.projectId || !rescheduleDueAt.value) return;
    rescheduleSaving.value = true;
    try {
        await updateReminder(data.projectId, data.reminderId, { dueAt: rescheduleDueAt.value });
        notificationsStore.markAsRead(data.notificationId);
        toast.removeGroup('reminder');
        rescheduleDialogVisible.value = false;
        resetRescheduleForm();
        toast.add({
            severity: 'success',
            summary: 'Rescheduled',
            detail: 'Reminder has been rescheduled',
            life: 4000
        });
    } catch (err) {
        const errorMsg = err.response?.data?.message ?? err.response?.data?.errors?.due_at?.[0] ?? 'Failed to reschedule reminder';
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMsg,
            life: 6000
        });
    } finally {
        rescheduleSaving.value = false;
    }
}

// Chat window state
const { isChatWindowVisible, closeChatWindow, minimizeChatWindow } = useChatWindow();

let authCheckInterval = null;

watchEffect(() => {
    hideSidebar.value = route.meta.hideSidebar;
});

// Initialize auth on app start
onMounted(() => {
    authStore.initializeAuth();
    // Ensure chat overlay is never shown on load (in case state was stuck)
    closeChatWindow();
    // Remove any stuck PrimeVue overlay masks (Dialog/Drawer can leave these in body)
    nextTick(() => {
        ['p-component-overlay', 'p-overlay-mask', 'p-dialog-mask'].forEach((cls) => {
            document.querySelectorAll(`.${cls}`).forEach((el) => el.remove());
        });
    });
    // Check auth status every 5 seconds
    authCheckInterval = setInterval(() => {
        const isStillAuthenticated = authStore.checkAuthStatus();
        if (!isStillAuthenticated && !isLoginPage.value) {
            // User is no longer authenticated, redirect to login
            router.push('/login');
        }
    }, 5000);
});

onUnmounted(() => {
    if (authCheckInterval) {
        clearInterval(authCheckInterval);
    }
});

// Full-screen layout (no sidebar) for splash, login, and 404
const isLoginPage = computed(() => {
    const onboarding = route.matched.some((m) => m.meta?.onboarding === true);
    return (
        route.name === 'Splash' ||
        route.name === 'Login' ||
        route.name === 'Register' ||
        onboarding ||
        route.name === 'AcceptInvite' ||
        route.name === 'NotFound'
    );
});

// Computed properties for role indicator
const isAuthenticated = computed(() => authStore.isLoggedIn);
const showFreelancerSidebar = computed(() => authStore.isFreelanceUser);

// Real-time notification toasts (polls when authenticated and tab visible)
useNotificationToasts(isAuthenticated);
const currentUser = computed(() => authStore.currentUser);
const userRole = computed(() => authStore.currentUser?.role?.name);

const roleDisplayName = computed(() => {
    // Use the display_name from the API response if available
    return currentUser.value?.role?.display_name || 'No Role';
});

const userDisplayName = computed(() => {
    const user = currentUser.value;
    if (!user) return 'No User';
    
    // Try different name fields in order of preference
    if (user.name) return user.name;
    if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
    if (user.first_name) return user.first_name;
    if (user.username) return user.username;
    if (user.email) return user.email;
    
    return 'Unknown User';
});

const roleBarClass = computed(() => {
    if (!userRole.value) {
        return 'bg-gray-500'; // Gray for no role
    }
    
    switch (userRole.value) {
        case 'system_admin':
            return 'bg-red-600'; // Red for system admin
        case 'company_admin':
            return 'bg-blue-600'; // Blue for company admin
        case 'company_user':
            return 'bg-green-600'; // Green for company user
        case 'freelance_user':
            return 'bg-amber-600'; // Amber for freelancer
        default:
            return 'bg-gray-500'; // Gray for unknown roles
    }
});
</script>

<style scoped>
/* No custom styles needed as we're using Tailwind */
</style>
